import { isText } from '../../helpers/dom/node';
import { expandWhitespace } from '../../helpers/textToHtml';
import { DEFAULT_TEXT_UNDER_CURSOR, TextUnderCursor } from '../../helpers/textUnderCursor';
import { BaseService } from '../base/service';
import { TextUnderCursorRange, TextUnderCursorService } from '../text/textUnderCursorService';
import { ElementSelectionService } from './elementSelectionService';

export function createElementTextUnderCursorService(
  selectionService: ElementSelectionService,
): TextUnderCursorService {
  return new ElementTextUnderCursorService(selectionService);
}

interface ElementTextUnderCursorState {
  textNode: Text;
  anchorOffset: number;
  focusOffset: number;
  range: Range;
}

export class ElementTextUnderCursorService extends BaseService implements TextUnderCursorService {
  constructor(selectionService: ElementSelectionService) {
    super(selectionService);

    this.selectionService = selectionService;
  }

  private readonly selectionService: ElementSelectionService;

  private get state(): ElementTextUnderCursorState | undefined {
    const { selection } = this.selectionService;
    if (!selection) {
      return undefined;
    }

    const { anchorNode, anchorOffset, focusNode, focusOffset, range } = selection;
    if (!isText(focusNode) || anchorNode !== focusNode) {
      return undefined;
    }

    return {
      textNode: focusNode,
      anchorOffset,
      focusOffset,
      range,
    };
  }

  get isActive() {
    return this.selectionService.isActive;
  }

  get textUnderCursor() {
    const { state } = this;
    if (!state) {
      return DEFAULT_TEXT_UNDER_CURSOR;
    }

    const { textNode, anchorOffset, focusOffset } = state;
    return {
      text: textNode.data,
      anchorOffset,
      focusOffset,
    };
  }

  getBoundingClientRect(textRange?: TextUnderCursorRange): ClientRect | undefined {
    const { state } = this;
    if (!state) {
      return undefined;
    }

    const { textNode, range } = state;
    const measureRange = range.cloneRange();

    if (textRange) {
      try {
        measureRange.setStart(textNode, textRange.startOffset);
        measureRange.setEnd(textNode, textRange.endOffset);
      } catch {
        // Indexes are out of range.
        // TODO: Catch only expected exceptions (DOMException, RangErro, IndexSizeError)?
        return undefined;
      }
    }

    return measureRange.getBoundingClientRect();
  }

  setTextUnderCursor(textUnderCursor: Readonly<TextUnderCursor>): void {
    const { state } = this;
    if (!state) {
      return;
    }

    const { textNode } = state;
    textNode.data = expandWhitespace(textUnderCursor.text);

    const { anchorOffset, focusOffset } = textUnderCursor;
    this.selectionService.selectWithOffsetsInContainers({
      anchorNode: textNode,
      anchorOffset,
      focusNode: textNode,
      focusOffset,
    });
  }
}
